/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEqual, sortBy } from 'lodash';
import QueryString from 'query-string';
import { openModal } from '../../actions/modalActions';
import { cadenceWorkPackagesSelector } from '../../selectors/workPackageSelector';
import {
  removeWorkPackageFromCadence,
  addWorkPackageToCadence,
  fetchTargetWorkPackages
} from '../../actions/workPackageActions';
import modalTypes from '../../constants/modalTypes';
import { CadenceStatuses } from '../../constants/storyStatusTypes';
import GrayPlus from '../Buttons/grayPlus';
import CadenceRow from './cadenceRow';
import { allowsUser } from '../../utils/authorization';
import { updateUserSpaceView } from '../../actions/userActions';
import { updateUserWorkspace } from '../../utils/userHelpers';
import ErrorBoundaryWrapper from '../ErrorBoundaries/ErrorBoundaryWrapper';

const initialState = {
  tfil: '',
  pfil: '',
  gfil: '',
  sc: false,
  // open: 'YWxs',
  mod: '',
  tf: ''
};

class CadenceWall extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCompleted: false,
      preservedState: initialState,
      cadences: props.cadences
    };
  }

  componentWillMount() {
    if (this.props.location.search) this.processState();
  }

  componentDidMount() {
    document.title = 'Cadence';
  }

  componentDidUpdate(prevProps) {
    const { cadences } = this.props;
    if (!isEqual(prevProps.cadences, cadences)) {
      this.setState({ cadences });
    }
  }

  componentWillUnmount() {
    this.props.updateUserSpaceView({ user: updateUserWorkspace() });
  }

  changePath = (state) => {
    const { location, history } = this.props;
    const path = location.pathname;
    const endingSlash = path[path.length - 1] === '/' ? '' : '/';
    history.replace(`${path}${endingSlash}?${QueryString.stringify(state)}`);
  };

  addSprint = () =>
    this.props.openModal(modalTypes.cadenceDetail, { cadence: {} });

  processState = () => {
    const { location, history } = this.props;
    const { preservedState } = this.state;
    const path = location.pathname;
    let newPreservedState;

    if (location.search) newPreservedState = QueryString.parse(location.search);
    else newPreservedState = preservedState;

    if (!isEqual(Object.keys(newPreservedState), Object.keys(preservedState))) {
      if (!isEqual(newPreservedState, preservedState)) {
        this.setState(
          {
            preservedState: { ...preservedState, ...newPreservedState },
            showCompleted: JSON.parse(newPreservedState.sc || 'false')
          },
          () => this.changePath(newPreservedState)
        );
      }
    } else {
      history.replace(path);
    }
  };

  saveState = (state) => {
    const { preservedState } = this.state;
    this.setState(
      { preservedState: { ...preservedState, ...state } },
      this.changePath(state)
    );
  };

  // mark a sprint as complete &&
  toggleCompleted = () => {
    const { showCompleted, preservedState } = this.state;
    this.setState({ showCompleted: !showCompleted }, () =>
      this.saveState({
        ...preservedState,
        sc: this.state.showCompleted
      })
    );
  };

  filterByTeam = (teamId) => {
    this.setState({
      cadences: this.props.cadences.filter((c) => c.team_id === teamId)
    });
  };

  render() {
    const { cadenceWorkPackages, showTrash } = this.props;
    const { showCompleted, preservedState, cadences } = this.state;
    const allCadences = sortBy(cadences, 'id').filter((c) => {
      if (showTrash) return true;
      return !c.deleted_at;
    });
    const allowCreate = allowsUser('create');

    return (
      <div className="container-fluid cadencewall planwall ">
        <div className="row">
          <div className="top row">
            {allowCreate && (
              <div className="col-md-2">
                <GrayPlus handleClick={this.addSprint} />
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={this.addSprint}
                >
                  Add Sprint
                </button>
              </div>
            )}
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-link"
                onClick={this.toggleCompleted}
              >
                {showCompleted ? 'Hide' : 'Show'} Completed Sprints
              </button>
            </div>
          </div>
          <div className="row">
            {allCadences.map((cadence) => (
              <div
                key={cadence.id}
                className={
                  cadence.status === CadenceStatuses.ACCEPTED && !showCompleted
                    ? 'hide'
                    : 'col-md-4 cadences'
                }
              >
                <CadenceRow
                  cadence={cadence}
                  results={cadenceWorkPackages[cadence.id] || []}
                  remove={this.props.removeWorkPackageFromCadence}
                  add={this.props.addWorkPackageToCadence}
                  showCompleted={showCompleted}
                  preservedState={preservedState}
                  preserveComponentState={this.saveState}
                  fromWall
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

CadenceWall.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  cadences: PropTypes.array,
  cadenceWorkPackages: PropTypes.object,
  openModal: PropTypes.func,
  fetchTargetWorkPackages: PropTypes.func,
  addWorkPackageToCadence: PropTypes.func,
  removeWorkPackageFromCadence: PropTypes.func
};

const mapStateToProps = (state) => {
  const selectedTeamId = state.workPackageReducer.selectedSpace.id; // must be team space for this wall to be visible
  return {
    cadences: state.cadenceReducer.cadences.filter(
      (c) => c.team_id === selectedTeamId
    ),
    cadenceWorkPackages: cadenceWorkPackagesSelector(state),
    showTrash: state.trashReducer.showTrash
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addWorkPackageToCadence,
      removeWorkPackageFromCadence,
      fetchTargetWorkPackages,
      openModal,
      updateUserSpaceView
    },
    dispatch
  );

export default ErrorBoundaryWrapper(
  connect(mapStateToProps, mapDispatchToProps)(CadenceWall)
);
